import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { supabase } from "@/integrations/supabase/client";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useV2List, distanceKm, calculatePrice, getDrivingDistanceKm, getZonePrice } from "@/hooks/useTransfersV2";
import { ArrowRight, Users, Luggage, Clock, MapPin, Check, Plane, Hotel } from "lucide-react";
import { toast } from "sonner";
import GoldenParticles from "@/components/GoldenParticles";
import { PayPalCheckout } from "@/components/PayPalCheckout";
import RouteMap from "@/components/RouteMap";
import LocationCombobox from "@/components/LocationCombobox";
import { usePaymentMethods } from "@/hooks/usePaymentMethods";
import { Banknote, CreditCard } from "lucide-react";

type LocationOption = {
  id: string;
  name: string;
  lat: number;
  lng: number;
  kind: "airport" | "hotel";
  zone_id?: string | null;
  code?: string;
};

export default function TransfersV2() {
  const [searchParams] = useSearchParams();
  const { data: airports = [] } = useV2List("transfer_airports_v2");
  const { data: hotels = [] } = useV2List("transfer_hotels_v2");
  const { data: zones = [] } = useV2List("transfer_zones");
  const { data: svcTypes = [] } = useV2List("transfer_service_types");
  const { data: vehicles = [] } = useV2List("transfer_vehicles_v2");
  const { data: rates = [] } = useV2List("transfer_km_rates");
  const { data: zonePrices = [] } = useV2List("transfer_zone_prices");

  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [tripType, setTripType] = useState<"oneway" | "roundtrip">("oneway");
  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [passengers, setPassengers] = useState(2);
  const [luggage, setLuggage] = useState(2);
  const [pickupDate, setPickupDate] = useState("");
  const [pickupTime, setPickupTime] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [returnTime, setReturnTime] = useState("");
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null);
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "", flight: "", notes: "" });
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  const [createdBooking, setCreatedBooking] = useState<any>(null);
  const [paypalOptions, setPaypalOptions] = useState<any>(null);
  const [paypalError, setPaypalError] = useState<string | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<"cash" | "paypal" | "stripe" | null>(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const { paymentMethods } = usePaymentMethods();

  // Prefill from URL params (coming from Home hero widget)
  useEffect(() => {
    if (prefilled) return;
    if (!airports.length && !hotels.length) return;
    const qFrom = searchParams.get("fromId");
    const qTo = searchParams.get("toId");
    if (!qFrom || !qTo) return;
    setFromId(qFrom);
    setToId(qTo);
    const d = searchParams.get("pickupDate"); if (d) setPickupDate(d);
    const t = searchParams.get("pickupTime"); if (t) setPickupTime(t);
    const tt = searchParams.get("tripType"); if (tt === "roundtrip" || tt === "oneway") setTripType(tt);
    const rd = searchParams.get("returnDate"); if (rd) setReturnDate(rd);
    const rt = searchParams.get("returnTime"); if (rt) setReturnTime(rt);
    const pax = searchParams.get("passengers"); if (pax) setPassengers(Math.max(1, Number(pax) || 2));
    setPrefilled(true);
    setStep(2);
  }, [airports.length, hotels.length, searchParams, prefilled]);

  // Handle Stripe checkout redirect back
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const bookingRef = searchParams.get("booking");
    const stripeCancel = searchParams.get("stripe_cancel");
    if (stripeCancel) {
      toast.error("Stripe payment was cancelled");
      return;
    }
    if (!sessionId || !bookingRef) return;
    (async () => {
      const { data, error } = await supabase.functions.invoke("verify-stripe-payment", {
        body: { session_id: sessionId, booking_number: bookingRef },
      });
      if (error || !data?.success) {
        toast.error(data?.error || error?.message || "Payment verification failed");
        return;
      }
      window.location.href = `/booking-confirmation?ref=${bookingRef}&type=transfer-v2`;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);




  const locations: LocationOption[] = useMemo(() => {
    const a = airports.map((x: any) => ({ id: `airport:${x.id}`, name: `${x.name} (${x.code})`, lat: Number(x.latitude), lng: Number(x.longitude), kind: "airport" as const, code: x.code }));
    const h = hotels.map((x: any) => ({ id: `hotel:${x.id}`, name: x.name, lat: Number(x.latitude), lng: Number(x.longitude), kind: "hotel" as const, zone_id: x.zone_id }));
    return [...a, ...h];
  }, [airports, hotels]);

  const from = locations.find((l) => l.id === fromId);
  const to = locations.find((l) => l.id === toId);
  const [kmState, setKmState] = useState<{ km: number; durationText?: string; source: "google" | "fallback" | "haversine" }>({ km: 0, source: "haversine" });
  const km = kmState.km;
  const duration = kmState.durationText ?? `${Math.round((km / 60) * 60)} min`;

  useEffect(() => {
    if (!from || !to) { setKmState({ km: 0, source: "haversine" }); return; }
    setKmState({ km: 0, source: "haversine" });
    const originAddr = `${from.name}, Dominican Republic`;
    const destAddr = `${to.name}, Dominican Republic`;
    getDrivingDistanceKm(originAddr, destAddr)
      .then((r) => setKmState({ km: r.km, durationText: r.durationText, source: r.source }));
  }, [from?.id, to?.id]);

  const priceFor = (serviceTypeId: string) => {
    // 1) Prefer zone-based price from transfer_zone_prices (airport + zone + service type)
    const airportLoc = from?.kind === "airport" ? from : to?.kind === "airport" ? to : null;
    const hotelLoc = from?.kind === "hotel" ? from : to?.kind === "hotel" ? to : null;
    const airportId = airportLoc ? airportLoc.id.replace(/^airport:/, "") : null;
    const zoneId = hotelLoc?.zone_id || null;

    if (airportId && zoneId) {
      const zp = zonePrices.find(
        (p: any) =>
          p.airport_id === airportId &&
          p.zone_id === zoneId &&
          p.service_type_id === serviceTypeId &&
          (p.is_active ?? true)
      );
      if (zp) {
        let p = getZonePrice(
          { base_price: Number(zp.base_price), max_pax: Number(zp.max_pax), extra_per_pax: Number(zp.extra_per_pax) },
          passengers
        ) ?? 0;
        if (tripType === "roundtrip") p *= 2;
        return Math.round(p * 100) / 100;
      }
    }

    // 2) Fallback: km-based rate
    const rate = rates.find((r: any) => r.service_type_id === serviceTypeId && r.zone_id === zoneId)
      || rates.find((r: any) => r.service_type_id === serviceTypeId && !r.zone_id);
    if (!rate) return null;
    const zone = zones.find((z: any) => z.id === zoneId);
    const multiplier = Number(zone?.price_multiplier ?? 1);
    const zoneBase = Number(zone?.base_price ?? 0);
    let p = calculatePrice(km, {
      base_fee: Number(rate.base_fee),
      price_per_km: Number(rate.price_per_km),
      minimum_price: Number(rate.minimum_price),
      free_km: Number(rate.free_km),
    }, multiplier, zoneBase);
    if (tripType === "roundtrip") p *= 2;
    return Math.round(p * 100) / 100;
  };

  const selectedVehicle = vehicles.find((v: any) => v.id === selectedVehicleId);
  const selectedServiceType = svcTypes.find((s: any) => s.id === selectedVehicle?.service_type_id);
  const subtotal = selectedVehicle ? priceFor(selectedVehicle.service_type_id) || 0 : 0;

  const discount = useMemo(() => {
    if (!promoApplied) return 0;
    return promoApplied.discount_type === "percentage"
      ? subtotal * (Number(promoApplied.discount_value) / 100)
      : Number(promoApplied.discount_value);
  }, [promoApplied, subtotal]);

  const total = Math.max(0, subtotal - discount);

  const applyPromo = async () => {
    if (!promoCode) return;
    const { data, error } = await supabase.from("transfer_promo_codes")
      .select("*").eq("code", promoCode.toUpperCase()).eq("is_active", true).maybeSingle();
    if (error || !data) { toast.error("Invalid promo code"); return; }
    if (data.min_booking_amount && subtotal < Number(data.min_booking_amount)) {
      toast.error(`Minimum booking $${data.min_booking_amount}`);
      return;
    }
    setPromoApplied(data);
    toast.success("Promo code applied!");
  };

  const handleSearch = () => {
    if (!fromId || !toId) { toast.error("Please select origin and destination"); return; }
    if (fromId === toId) { toast.error("Origin and destination must differ"); return; }
    if (!pickupDate || !pickupTime) { toast.error("Please select pickup date and time"); return; }
    if (tripType === "roundtrip" && (!returnDate || !returnTime)) { toast.error("Please select return date/time"); return; }
    setStep(2);
  };

  const submitBooking = async () => {
    if (!customer.name || !customer.email) { toast.error("Name and email required"); return; }
    if (!selectedVehicle || !from || !to) return;
    setSubmitting(true);
    const payload: any = {
      customer_name: customer.name,
      customer_email: customer.email,
      customer_phone: customer.phone,
      origin: { name: from.name, lat: from.lat, lng: from.lng, kind: from.kind, ref_id: from.id },
      destination: { name: to.name, lat: to.lat, lng: to.lng, kind: to.kind, ref_id: to.id },
      distance_km: Math.round(km * 100) / 100,
      duration_min: Math.round((km / 60) * 60),
      trip_type: tripType,
      service_type_id: selectedVehicle.service_type_id,
      vehicle_id: selectedVehicle.id,
      passengers,
      luggage,
      pickup_date: pickupDate,
      pickup_time: pickupTime,
      return_date: tripType === "roundtrip" ? returnDate : null,
      return_time: tripType === "roundtrip" ? returnTime : null,
      flight_number: customer.flight,
      special_requests: customer.notes,
      promo_code_id: promoApplied?.id ?? null,
      subtotal, discount, total,
      payment_status: 'pending',
      status: 'pending_payment',
    };
    const { data, error } = await supabase.from("transfer_bookings_v2").insert(payload).select().single();
    if (error) { setSubmitting(false); toast.error(error.message); return; }
    setCreatedBooking(data);
    // Load PayPal config for step 4
    try {
      const { data: pp, error: ppErr } = await supabase.functions.invoke('get-paypal-client-id');
      if (ppErr || !pp?.success || !pp?.client_id) {
        throw new Error(pp?.error || ppErr?.message || 'PayPal not configured');
      }
      setPaypalOptions({
        clientId: pp.client_id,
        currency: "USD",
        intent: "capture" as const,
        "enable-funding": "card,venmo,paylater",
        components: "buttons,marks",
        locale: "en_US",
      });
      setPaypalError(null);
    } catch (e: any) {
      setPaypalError(e.message || 'PayPal not configured');
    }
    setSubmitting(false);
    toast.success("Reservation created. Complete payment to confirm.");
    setStep(4);
  };

  return (
    <Layout>
      <GoldenParticles />
      <section className="relative pt-24 pb-16 bg-gradient-to-b from-background via-background to-muted/20">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Badge className="bg-primary/10 text-primary border-primary/20 mb-4">TROPUNCA VIP TRANSFERS</Badge>
            <h1 className="text-5xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-primary via-primary to-primary/70 bg-clip-text text-transparent">
              Book Your Luxury Transfer
            </h1>
            <p className="text-muted-foreground text-lg">Airports, resorts, and destinations across the Dominican Republic</p>
          </div>

          {/* Steps indicator */}
          <div className="flex items-center justify-center gap-4 mb-10">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className="flex items-center gap-2">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${step >= n ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                  {step > n ? <Check className="w-5 h-5" /> : n}
                </div>
                <span className="hidden md:inline text-sm">
                  {n === 1 ? "Search" : n === 2 ? "Select Vehicle" : n === 3 ? "Details" : "Payment"}
                </span>
                {n < 4 && <ArrowRight className="w-4 h-4 text-muted-foreground" />}
              </div>
            ))}
          </div>

          {step === 1 && (
            <Card className="max-w-4xl mx-auto p-6 md:p-8 bg-card/80 backdrop-blur border-primary/20">
              <div className="flex gap-2 mb-6">
                <Button variant={tripType === "oneway" ? "default" : "outline"} onClick={() => setTripType("oneway")}>One Way</Button>
                <Button variant={tripType === "roundtrip" ? "default" : "outline"} onClick={() => setTripType("roundtrip")}>Round Trip</Button>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>From</Label>
                  <LocationSelect value={fromId} onChange={setFromId} options={locations} />
                </div>
                <div>
                  <Label>To</Label>
                  <LocationSelect value={toId} onChange={setToId} options={locations} exclude={fromId} />
                </div>
                <div>
                  <Label>Pickup Date</Label>
                  <Input type="date" value={pickupDate} onChange={(e) => setPickupDate(e.target.value)}
                    onClick={(e) => (e.currentTarget as any).showPicker?.()}
                    onFocus={(e) => (e.currentTarget as any).showPicker?.()} className="cursor-pointer" />
                </div>
                <div>
                  <Label>Pickup Time</Label>
                  <Input type="time" value={pickupTime} onChange={(e) => setPickupTime(e.target.value)}
                    onClick={(e) => (e.currentTarget as any).showPicker?.()}
                    onFocus={(e) => (e.currentTarget as any).showPicker?.()} className="cursor-pointer" />
                </div>
                {tripType === "roundtrip" && (
                  <>
                    <div>
                      <Label>Return Date</Label>
                      <Input type="date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)}
                        onClick={(e) => (e.currentTarget as any).showPicker?.()}
                        onFocus={(e) => (e.currentTarget as any).showPicker?.()} className="cursor-pointer" />
                    </div>
                    <div>
                      <Label>Return Time</Label>
                      <Input type="time" value={returnTime} onChange={(e) => setReturnTime(e.target.value)}
                        onClick={(e) => (e.currentTarget as any).showPicker?.()}
                        onFocus={(e) => (e.currentTarget as any).showPicker?.()} className="cursor-pointer" />
                    </div>
                  </>
                )}

                <div>
                  <Label>Passengers</Label>
                  <Input type="number" min={1} value={passengers} onChange={(e) => setPassengers(Number(e.target.value))} />
                </div>
                <div>
                  <Label>Luggage</Label>
                  <Input type="number" min={0} value={luggage} onChange={(e) => setLuggage(Number(e.target.value))} />
                </div>
              </div>

              <Button size="lg" className="w-full mt-6 h-14 text-lg" onClick={handleSearch}>
                Search Available Vehicles <ArrowRight className="ml-2" />
              </Button>
            </Card>
          )}

          {step === 2 && from && to && (
            <div className="max-w-5xl mx-auto space-y-6">
              <RouteMap
                from={{ name: from.name, lat: from.lat, lng: from.lng }}
                to={{ name: to.name, lat: to.lat, lng: to.lng }}
              />
              <Card className="p-4 flex flex-wrap items-center gap-4 bg-muted/30">
                <div className="flex items-center gap-2"><MapPin className="w-5 h-5 text-primary" /><span className="text-sm">{from.name}</span></div>
                <ArrowRight className="w-4 h-4 text-muted-foreground" />
                <div className="flex items-center gap-2"><MapPin className="w-5 h-5 text-primary" /><span className="text-sm">{to.name}</span></div>
                <div className="ml-auto flex gap-4 text-sm">
                  <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> ~{duration}</span>
                  <span>{km.toFixed(1)} km</span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setStep(1)}>Edit</Button>
              </Card>

              <div className="grid md:grid-cols-2 gap-4">
                {vehicles.filter((v: any) => v.passenger_capacity >= passengers).map((v: any) => {
                  const svc = svcTypes.find((s: any) => s.id === v.service_type_id);
                  const price = priceFor(v.service_type_id);
                  if (price == null) return null;
                  const isSelected = selectedVehicleId === v.id;
                  return (
                    <Card key={v.id} className={`p-6 cursor-pointer transition-all hover:shadow-lg hover:border-primary/50 ${isSelected ? "border-primary bg-primary/5" : ""}`} onClick={() => setSelectedVehicleId(v.id)}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <Badge variant="outline" className="mb-2">{svc?.name}</Badge>
                          <h3 className="text-xl font-bold">{v.name}</h3>
                          <p className="text-sm text-muted-foreground">{v.description}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-3xl font-bold text-primary">${price}</div>
                          <div className="text-xs text-muted-foreground">{tripType === "roundtrip" ? "Round trip" : "One way"}</div>
                        </div>
                      </div>
                      {v.image_url && <img src={v.image_url} alt={v.name} className="w-full h-40 object-cover rounded-lg mb-3" />}
                      <div className="flex gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {v.passenger_capacity}</span>
                        <span className="flex items-center gap-1"><Luggage className="w-4 h-4" /> {v.luggage_capacity}</span>
                      </div>
                      {Array.isArray(v.features) && v.features.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-3">
                          {v.features.slice(0, 4).map((f: string, i: number) => (
                            <Badge key={i} variant="secondary" className="text-xs">{f}</Badge>
                          ))}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>

              <Button size="lg" className="w-full h-14" disabled={!selectedVehicleId} onClick={() => setStep(3)}>
                Continue to Booking <ArrowRight className="ml-2" />
              </Button>
            </div>
          )}

          {step === 3 && selectedVehicle && (
            <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-6">
              <Card className="md:col-span-2 p-6 space-y-4">
                <h2 className="text-2xl font-bold">Your Details</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div><Label>Full Name *</Label><Input value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} /></div>
                  <div><Label>Email *</Label><Input type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} /></div>
                  <div><Label>Phone</Label><Input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} /></div>
                  <div><Label>Flight Number</Label><Input value={customer.flight} onChange={(e) => setCustomer({ ...customer, flight: e.target.value })} /></div>
                </div>
                <div><Label>Special Requests</Label><Textarea value={customer.notes} onChange={(e) => setCustomer({ ...customer, notes: e.target.value })} rows={3} /></div>
                <div className="pt-4 border-t">
                  <Label>Promo Code</Label>
                  <div className="flex gap-2">
                    <Input value={promoCode} onChange={(e) => setPromoCode(e.target.value)} placeholder="Enter code" />
                    <Button variant="outline" onClick={applyPromo}>Apply</Button>
                  </div>
                  {promoApplied && <Badge className="mt-2 bg-green-500/10 text-green-700 border-green-500/20">✓ {promoApplied.code} applied</Badge>}
                </div>
              </Card>

              <Card className="p-6 space-y-4 h-fit sticky top-24">
                <h3 className="font-bold text-lg">Booking Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Service</span><span>{selectedServiceType?.name}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Vehicle</span><span>{selectedVehicle.name}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Distance</span><span>{km.toFixed(1)} km</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Passengers</span><span>{passengers}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Trip type</span><span>{tripType === "roundtrip" ? "Round Trip" : "One Way"}</span></div>
                </div>
                <div className="border-t pt-4 space-y-1">
                  <div className="flex justify-between"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                  {discount > 0 && <div className="flex justify-between text-green-600"><span>Discount</span><span>-${discount.toFixed(2)}</span></div>}
                  <div className="flex justify-between text-xl font-bold pt-2"><span>Total</span><span className="text-primary">${total.toFixed(2)}</span></div>
                </div>
                <Button size="lg" className="w-full" disabled={submitting} onClick={submitBooking}>
                  {submitting ? "Creating..." : "Continue to Payment"}
                </Button>
                <Button variant="ghost" size="sm" className="w-full" onClick={() => setStep(2)}>Back</Button>
              </Card>
            </div>
          )}

          {step === 4 && createdBooking && (
            <div className="max-w-3xl mx-auto space-y-6">
              <Card className="p-6 bg-primary/5 border-primary/20">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <Check className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">Reservation created</h3>
                    <p className="text-sm text-muted-foreground">
                      Booking # <span className="font-mono">{createdBooking.booking_number}</span> — Complete payment to confirm.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold">Choose Payment Method</h2>
                  <div className="text-2xl font-bold text-primary">${total.toFixed(2)}</div>
                </div>

                {paymentMethods.length === 0 && (
                  <div className="p-4 rounded-md bg-muted/50 text-sm text-muted-foreground">
                    No payment methods are enabled. Please contact us to complete your booking.
                  </div>
                )}

                <div className="grid gap-3">
                  {paymentMethods.map((pm) => {
                    const isSelected = selectedPayment === pm.method_type;
                    const label = pm.method_type === "cash" ? "Pay in Cash" : pm.method_type === "paypal" ? "PayPal" : "Credit / Debit Card (Stripe)";
                    const desc = pm.method_type === "cash"
                      ? "Reserve now, pay in cash on arrival. Confirmation is emailed immediately."
                      : pm.method_type === "paypal"
                      ? "Secure payment via PayPal or credit card. Booking is confirmed after payment."
                      : "Secure card payment via Stripe Checkout. Booking is confirmed after payment.";
                    const Icon = pm.method_type === "cash" ? Banknote : pm.method_type === "paypal" ? CreditCard : CreditCard;
                    return (
                      <button
                        key={pm.id}
                        type="button"
                        onClick={() => setSelectedPayment(pm.method_type)}
                        className={`text-left p-4 rounded-lg border-2 transition-all ${isSelected ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}
                      >
                        <div className="flex items-start gap-3">
                          <Icon className="w-6 h-6 text-primary mt-0.5" />
                          <div className="flex-1">
                            <div className="font-semibold">{label}</div>
                            <div className="text-sm text-muted-foreground">{desc}</div>
                          </div>
                          {isSelected && <Check className="w-5 h-5 text-primary" />}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Cash flow */}
                {selectedPayment === "cash" && (
                  <Button
                    size="lg"
                    className="w-full"
                    disabled={processingPayment}
                    onClick={async () => {
                      setProcessingPayment(true);
                      const { error } = await supabase
                        .from("transfer_bookings_v2")
                        .update({ payment_method: "cash", payment_status: "pending_cash", status: "confirmed" })
                        .eq("booking_number", createdBooking.booking_number);
                      if (error) { toast.error(error.message); setProcessingPayment(false); return; }
                      try {
                        await supabase.functions.invoke("notify-transfer-v2", {
                          body: { booking_number: createdBooking.booking_number },
                        });
                      } catch (e) { console.error(e); }
                      toast.success("Booking confirmed! Check your email.");
                      window.location.href = `/booking-confirmation?ref=${createdBooking.booking_number}&type=transfer-v2`;
                    }}
                  >
                    {processingPayment ? "Confirming..." : "Confirm Cash Booking"}
                  </Button>
                )}

                {/* PayPal flow */}
                {selectedPayment === "paypal" && (
                  <div>
                    {paypalError && (
                      <div className="p-4 rounded-md bg-destructive/10 text-destructive text-sm mb-3">
                        {paypalError}
                      </div>
                    )}
                    {paypalOptions && !paypalError && (
                      <PayPalScriptProvider options={paypalOptions}>
                        <PayPalCheckout
                          booking={{
                            booking_number: createdBooking.booking_number,
                            from_location: (createdBooking.origin as any)?.name,
                            to_location: (createdBooking.destination as any)?.name,
                            customer_name: createdBooking.customer_name,
                            email: createdBooking.customer_email,
                            phone: createdBooking.customer_phone,
                          }}
                          amount={total.toFixed(2)}
                          currency="USD"
                          onSuccess={async () => {
                            await supabase
                              .from("transfer_bookings_v2")
                              .update({ payment_method: "paypal" })
                              .eq("booking_number", createdBooking.booking_number);
                            try {
                              await supabase.functions.invoke("notify-transfer-v2", {
                                body: { booking_number: createdBooking.booking_number },
                              });
                            } catch (e) { console.error(e); }
                            window.location.href = `/booking-confirmation?ref=${createdBooking.booking_number}&type=transfer-v2`;
                          }}
                        />
                      </PayPalScriptProvider>
                    )}
                  </div>
                )}

                {/* Stripe flow */}
                {selectedPayment === "stripe" && (
                  <Button
                    size="lg"
                    className="w-full"
                    disabled={processingPayment}
                    onClick={async () => {
                      setProcessingPayment(true);
                      await supabase
                        .from("transfer_bookings_v2")
                        .update({ payment_method: "stripe" })
                        .eq("booking_number", createdBooking.booking_number);
                      const origin = window.location.origin;
                      const { data, error } = await supabase.functions.invoke("create-stripe-checkout", {
                        body: {
                          booking_number: createdBooking.booking_number,
                          amount: total.toFixed(2),
                          currency: "USD",
                          return_url: `${origin}/transfers?booking=${createdBooking.booking_number}`,
                          cancel_url: `${origin}/transfers?stripe_cancel=1&booking=${createdBooking.booking_number}`,
                        },
                      });
                      if (error || !data?.success || !data?.url) {
                        toast.error(data?.error || error?.message || "Failed to start Stripe checkout");
                        setProcessingPayment(false);
                        return;
                      }
                      window.location.href = data.url;
                    }}
                  >
                    {processingPayment ? "Redirecting to Stripe..." : "Pay with Card (Stripe)"}
                  </Button>
                )}

                <Button variant="ghost" size="sm" className="w-full" onClick={() => setStep(3)}>
                  Back to details
                </Button>
              </Card>
            </div>
          )}

        </div>
      </section>
    </Layout>
  );
}

function LocationSelect(props: { value: string; onChange: (v: string) => void; options: LocationOption[]; exclude?: string }) {
  return <LocationCombobox {...props} placeholder="-- Select location --" />;
}

